import classnames from 'classnames';
import React, { FC, memo, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { BiAddMediaActionTarget, BiAddMediaClickOrigin } from '../../../../constants/Bi';
import { DragType } from '../../../../constants/DragType';
import { stopPropagation } from '../../../../utils/dom/domEvents';
import { ActionClickOrigin } from '../../../../utils/hooks/bi/useTrackActions/useTrackActions.types';
import { TrackBoardClickTarget, useTrackBoardAnalytics } from '../../../../utils/hooks/bi/useTrackBoardAnalytics';
import { useOpenContextMenu } from '../../../../utils/hooks/useOpenContextMenu';
import { useSources } from '../../../../utils/hooks/useSources';
import { isItemRenamable, isJustCreatedFolderId } from '../../../../utils/media-manager-sdk/items';
import { DragSource, DropZone, FolderThumbnail, IconButton, Input, Text, Tooltip } from '../../../common';
import { BoardsContextMenuTrigger } from '../../../common/BoardsContextMenu';
import { Bookmark, BookmarkFilled, MoreSmall, RenameSmall } from '../../../common/WixIcons';
import { GenericItem } from '../../Generic/GenericItem';
import baseItemStyles from '../ListItemBase.scss';
import folderStyles from './ListFolder.scss';
import { IOuter } from './ListFolder.types';

const accepts = [DragType.Folder, DragType.File];

const ListFolderComponent: FC<IOuter> = ({
  item,
  className,
  selected,
  isEditing,
  isCut,
  onClick,
  onContextMenu,
  onNameChange,
  onRenameStart,
  onRenameComplete,
  onRenameCancel,
  onDoubleClick,
  onDrop,
  beginDrag,
  canDrag,
  selectedItems,
  onRemoveItemsFromBoard,
  source,
}) => {
  const handleRenameStart = useCallback(
    (event) => {
      stopPropagation(event);
      onRenameStart(item.id);
    },
    [onRenameStart, item],
  );

  const boardsContextMenuTriggerRef = useRef();
  const openBoardsContextMenu = useOpenContextMenu(boardsContextMenuTriggerRef);

  const { trackBoardClick } = useTrackBoardAnalytics();

  const handleAddToBoardClick = useCallback(
    (event) => {
      openBoardsContextMenu(event);
      trackBoardClick(item, {
        target: TrackBoardClickTarget.Add,
        clickOrigin: ActionClickOrigin.File,
      });
    },
    [openBoardsContextMenu, trackBoardClick, item],
  );

  const handleRemoveSelectedItemsFromBoard = useCallback(() => {
    const clickOrigin = ActionClickOrigin.File;

    onRemoveItemsFromBoard([item], { clickOrigin });
    trackBoardClick(item, {
      target: TrackBoardClickTarget.Remove,
      clickOrigin,
    });
  }, [item, onRemoveItemsFromBoard, trackBoardClick]);

  const { isBoardRoot } = useSources({ source });

  return (
    <GenericItem
      item={item}
      selected={selected}
      className={classnames(
        baseItemStyles.itemWrapper,
        {
          [baseItemStyles.selected]: selected,
          [baseItemStyles.isCut]: isCut,
          [baseItemStyles.isEditing]: isEditing,
        },
        className,
      )}
      itemClassName={baseItemStyles.genericItemWrapper}
      lassoItemClassName={folderStyles.lassoItem}
      onDrop={onDrop}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onNameChange={onNameChange}
      onRenameComplete={onRenameComplete}
      onRenameCancel={onRenameCancel}
      onDoubleClick={onDoubleClick}
      dataHook="list-folder"
      canDrag={canDrag}
      selectedItems={selectedItems}
    >
      {({
        handleNameChange,
        handleRenameCancel,
        handleRenameComplete,
        handleDropOnFolder,
        openContextMenu,
        canDrag,
      }) => (
        <DragSource
          className={baseItemStyles.dragSource}
          item={item}
          beginDrag={beginDrag}
          type={DragType.Folder}
          disabled={!canDrag}
        >
          <DropZone
            className={classnames(baseItemStyles.item, folderStyles.folder)}
            activeClassName={folderStyles.dropZoneActive}
            accepts={accepts}
            onDrop={handleDropOnFolder}
            disabled={isJustCreatedFolderId(item.id)}
            biOrigin={BiAddMediaClickOrigin.DragAndDrop}
            biTarget={BiAddMediaActionTarget.FolderDragAndDrop}
            item={item}
          >
            <div className={baseItemStyles.info}>
              <div className={folderStyles.thumbnailWrapper}>
                <FolderThumbnail
                  className={classnames(baseItemStyles.thumbnail, folderStyles.thumbnail)}
                  folder={item}
                />
              </div>

              <div className={baseItemStyles.nameWrapper}>
                {isEditing ? (
                  <Input
                    value={item.name}
                    onChange={handleNameChange}
                    onBlur={handleRenameComplete}
                    onEnterPressed={handleRenameComplete}
                    onEscapePressed={handleRenameCancel}
                    autoFocus
                    dataHook="list-folder-name-edit-input"
                    className={baseItemStyles.editItemNameInput}
                  />
                ) : (
                  <Text
                    weight="normal"
                    size="small"
                    tagName="div"
                    className={baseItemStyles.name}
                    title={item.name}
                    dataHook="list-folder-name"
                  >
                    {item.name}
                  </Text>
                )}
              </div>
            </div>

            <div className={baseItemStyles.actions}>
              {isBoardRoot ? (
                <Tooltip content={<FormattedMessage id="actions.remove-from-board" />} maxWidth={230} appendTo="window">
                  <IconButton
                    skin="light"
                    size="small"
                    className={baseItemStyles.button}
                    onClick={handleRemoveSelectedItemsFromBoard}
                    dataHook="remove-from-board-button"
                  >
                    <BookmarkFilled />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip content={<FormattedMessage id="actions.add-to-board" />} maxWidth={230} appendTo="window">
                  <BoardsContextMenuTrigger
                    contextMenuRef={boardsContextMenuTriggerRef}
                    clickOrigin={ActionClickOrigin.File}
                  >
                    <IconButton
                      skin="light"
                      size="small"
                      className={baseItemStyles.button}
                      onClick={handleAddToBoardClick}
                      dataHook="add-to-board-button"
                    >
                      <Bookmark />
                    </IconButton>
                  </BoardsContextMenuTrigger>
                </Tooltip>
              )}
              {isItemRenamable(item) && (
                <Tooltip content={<FormattedMessage id="actions.rename" />} maxWidth={230} appendTo="window">
                  <IconButton
                    skin="light"
                    size="small"
                    className={baseItemStyles.button}
                    onClick={handleRenameStart}
                    dataHook="list-folder-rename-button"
                  >
                    <RenameSmall />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip content={<FormattedMessage id="actions.more-actions" />} maxWidth={230} appendTo="window">
                <IconButton
                  skin="light"
                  size="small"
                  className={baseItemStyles.button}
                  onClick={openContextMenu}
                  dataHook="three-dots"
                >
                  <MoreSmall />
                </IconButton>
              </Tooltip>
            </div>
          </DropZone>
        </DragSource>
      )}
    </GenericItem>
  );
};

export const ListFolder: FC<IOuter> = memo<IOuter>(ListFolderComponent);
